import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CaretLeft, Warning, Package as PackageIcon, Gift } from "@phosphor-icons/react";
import { formatCurrency } from "@/lib/utils-format";

const CURRENCIES = ["EUR", "USD", "GBP", "INR"];
const PAYMENT_METHODS = ["Bank Transfer", "PayPal", "Stripe", "Western Union", "MoneyGram", "UPI", "Cash", "Other"];

export default function NewOrder() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dupes, setDupes] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [form, setForm] = useState({
    customer_id: "", customer_name: "", company: "", email: "", phone: "",
    address: "", country: "", product: "", quantity: 1, weight: 0,
    total_amount: 0, currency: "EUR",
    payment_method: "Bank Transfer", payment_reference: "", remarks: "",
  });

  useEffect(() => {
    api.get("/products", { params: { active_only: true } }).then((r) => setProducts(r.data)).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const selected = products.find((p) => p.product_id === selectedProductId);
  const selectedCustomer = customers.find((c) => c.customer_id === selectedCustomerId);

  // Bulk promo: bonus_qty = floor(qty / threshold) * bonus_qty, if both > 0.
  const computeBonus = (product, qty) => {
    if (!product || !product.bonus_threshold || !product.bonus_quantity) return 0;
    return Math.floor((qty || 0) / product.bonus_threshold) * product.bonus_quantity;
  };
  // Customer overrides win over product tiers.
  const effectiveTiers = (product, customer) => {
    if (customer && customer.pricing_overrides?.length) {
      const ov = customer.pricing_overrides.find((o) => o.product_id === product?.product_id);
      if (ov && ov.price_tiers?.length) return ov.price_tiers;
    }
    return product?.price_tiers || [];
  };
  const applicableUnitPrice = (product, qty, customer) => {
    if (!product) return 0;
    const tiers = effectiveTiers(product, customer).slice().sort((a, b) => a.min_qty - b.min_qty);
    const match = tiers.filter((t) => t.min_qty <= qty).pop();
    return match ? match.unit_price : product.total_price;
  };
  const currentQty = parseInt(form.quantity, 10) || 0;
  const currentBonus = computeBonus(selected, currentQty);
  const currentShipped = currentQty + currentBonus;
  const currentUnitPrice = selected ? applicableUnitPrice(selected, currentQty, selectedCustomer) : 0;
  const isCustomerPricing = selected && selectedCustomer?.pricing_overrides?.some((o) => o.product_id === selected.product_id);

  const pickCustomer = (customer_id) => {
    setSelectedCustomerId(customer_id);
    const c = customers.find((x) => x.customer_id === customer_id);
    if (!c) return;
    setForm((f) => ({
      ...f,
      customer_id: c.customer_id,
      customer_name: c.name || "",
      company: c.company || "",
      email: c.email || "",
      phone: c.phone || "",
      address: c.address || f.address,
      country: c.country || f.country,
    }));
    // Recompute price with customer overrides if a product is already picked
    if (selected) {
      const qty = parseInt(form.quantity, 10) || 1;
      const unitPrice = applicableUnitPrice(selected, qty, c);
      setForm((f) => ({ ...f, total_amount: +(unitPrice * qty).toFixed(2) }));
    }
  };

  const pickProduct = (product_id) => {
    setSelectedProductId(product_id);
    const p = products.find((x) => x.product_id === product_id);
    if (!p) return;
    const qty = parseInt(form.quantity, 10) || 1;
    const bonus = computeBonus(p, qty);
    const shipped = qty + bonus;
    const unitPrice = applicableUnitPrice(p, qty, selectedCustomer);
    setForm((f) => ({
      ...f,
      product: `${p.name}${p.unit_size ? " · " + p.unit_size : ""}`,
      product_id: p.product_id,
      weight: +(p.weight_kg * shipped).toFixed(3),
      total_amount: +(unitPrice * qty).toFixed(2),
      currency: p.currency,
    }));
  };

  const updateQuantity = (v) => {
    const qty = parseInt(v, 10) || 1;
    set("quantity", v);
    if (selected) {
      const bonus = computeBonus(selected, qty);
      const shipped = qty + bonus;
      const unitPrice = applicableUnitPrice(selected, qty, selectedCustomer);
      setForm((f) => ({
        ...f, quantity: v,
        weight: +(selected.weight_kg * shipped).toFixed(3),
        total_amount: +(unitPrice * qty).toFixed(2),
      }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const qty = parseInt(form.quantity, 10) || 1;
      const bonus = selected ? computeBonus(selected, qty) : 0;
      const unitPrice = selected ? applicableUnitPrice(selected, qty, selectedCustomer) : null;
      const res = await api.post("/orders", {
        ...form,
        customer_id: selectedCustomer?.customer_id || form.customer_id || null,
        product_id: selected?.product_id || form.product_id || null,
        quantity: qty,
        bonus_quantity: bonus,
        shipped_quantity: qty + bonus,
        unit_price: unitPrice,
        weight: parseFloat(form.weight) || 0,
        total_amount: parseFloat(form.total_amount) || 0,
      });
      if (res.data._duplicates?.length > 0) {
        setDupes(res.data._duplicates);
      }
      toast.success(`Order ${res.data.order_number} created`);
      navigate(`/orders/${res.data.order_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create order");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-4xl" data-testid="new-order-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 mb-4 transition-colors" data-testid="back-button">
        <CaretLeft size={14} /> Back
      </button>
      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">Create</p>
      <h1 className="text-4xl font-black tracking-tighter mb-8">New Order</h1>

      {dupes.length > 0 && (
        <div className="mb-6 border border-amber-300 bg-amber-50 p-4 flex gap-3" data-testid="duplicate-warning">
          <Warning size={20} className="text-amber-600 flex-shrink-0" weight="bold" />
          <div>
            <p className="text-sm font-semibold text-amber-900">Possible duplicates detected</p>
            <ul className="text-xs text-amber-800 mt-1 space-y-0.5">
              {dupes.map((d) => <li key={d.order_number}>#{d.order_number} — {d.customer_name}</li>)}
            </ul>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-8" data-testid="new-order-form">
        <Section title="Customer">
          {customers.length > 0 && (
            <Field label="Pick from directory" span={2}>
              <Select value={selectedCustomerId} onValueChange={pickCustomer}>
                <SelectTrigger className="border-zinc-300 rounded-md" data-testid="customer-directory-select">
                  <SelectValue placeholder="Select an existing customer (or enter manually below)" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.customer_id} value={c.customer_id}>
                      {c.name}{c.company ? ` · ${c.company}` : ""}
                      {c.pricing_overrides?.length > 0 ? "  ★" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedCustomer?.pricing_overrides?.length > 0 && (
                <p className="text-[10px] text-blue-700 mt-1 font-semibold" data-testid="customer-has-custom-pricing">
                  ★ Has custom pricing on {selectedCustomer.pricing_overrides.length} product(s)
                </p>
              )}
            </Field>
          )}
          <Field label="Name *"><Input required value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className="border-zinc-300 rounded-md" data-testid="customer-name-input" /></Field>
          <Field label="Company"><Input value={form.company} onChange={(e) => set("company", e.target.value)} className="border-zinc-300 rounded-md" data-testid="company-input" /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="border-zinc-300 rounded-md" data-testid="email-input" /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="border-zinc-300 rounded-md" data-testid="phone-input" /></Field>
          <Field label="Address *" span={2}><Input required value={form.address} onChange={(e) => set("address", e.target.value)} className="border-zinc-300 rounded-md" data-testid="address-input" /></Field>
          <Field label="Country *"><Input required value={form.country} onChange={(e) => set("country", e.target.value)} className="border-zinc-300 rounded-md" data-testid="country-input" /></Field>
        </Section>

        <Section title="Product">
          {products.length > 0 && (
            <Field label="Pick from catalog" span={2}>
              <Select value={selectedProductId} onValueChange={pickProduct}>
                <SelectTrigger className="border-zinc-300 rounded-md" data-testid="product-catalog-select">
                  <SelectValue placeholder="Select a product (or enter manually below)" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((p) => (
                    <SelectItem key={p.product_id} value={p.product_id}>
                      {p.name} · {p.unit_size} — {formatCurrency(p.total_price, p.currency)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          {selected && (
            <div className="col-span-2 border border-zinc-200 bg-zinc-50 p-3 flex items-center justify-between" data-testid="selected-product-summary">
              <div className="flex items-center gap-2">
                <PackageIcon size={16} weight="bold" className="text-zinc-600" />
                <div>
                  <p className="text-xs font-semibold">{selected.name} · {selected.unit_size}</p>
                  <p className="text-[10px] text-zinc-500 font-mono">
                    Product {formatCurrency(selected.product_cost, selected.currency)} + Packing {formatCurrency(selected.packing_cost, selected.currency)} + Shipping {formatCurrency(selected.shipping_cost, selected.currency)}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Unit price</p>
                <p className="text-lg font-black tracking-tighter font-mono" data-testid="active-unit-price">{formatCurrency(currentUnitPrice, selected.currency)}</p>
                {isCustomerPricing ? (
                  <p className="text-[10px] text-blue-700 font-semibold uppercase tracking-widest">★ Customer price</p>
                ) : (selected.price_tiers?.length > 0 && currentUnitPrice !== selected.total_price && (
                  <p className="text-[10px] text-blue-700 font-semibold uppercase tracking-widest">Tier price active</p>
                ))}
              </div>
            </div>
          )}
          <Field label="Product Description *" span={2}><Input required value={form.product} onChange={(e) => set("product", e.target.value)} className="border-zinc-300 rounded-md" data-testid="product-input" /></Field>
          <Field label="Quantity"><Input type="number" min="1" value={form.quantity} onChange={(e) => updateQuantity(e.target.value)} className="border-zinc-300 rounded-md" data-testid="quantity-input" /></Field>
          <Field label="Weight (kg)"><Input type="number" step="0.01" value={form.weight} onChange={(e) => set("weight", e.target.value)} className="border-zinc-300 rounded-md" data-testid="weight-input" /></Field>
        </Section>

        <Section title="Payment">
          <Field label="Amount *"><Input required type="number" step="0.01" value={form.total_amount} onChange={(e) => set("total_amount", e.target.value)} className="border-zinc-300 rounded-md" data-testid="amount-input" /></Field>
          <Field label="Currency">
            <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger className="border-zinc-300 rounded-md" data-testid="currency-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Payment Method">
            <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
              <SelectTrigger className="border-zinc-300 rounded-md" data-testid="payment-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Payment Reference"><Input value={form.payment_reference} onChange={(e) => set("payment_reference", e.target.value)} className="border-zinc-300 rounded-md" data-testid="payment-reference-input" /></Field>
          <Field label="Remarks" span={2}><Textarea value={form.remarks} onChange={(e) => set("remarks", e.target.value)} rows={3} className="border-zinc-300 rounded-md" data-testid="remarks-input" /></Field>
        </Section>

        <div className="flex gap-3">
          <Button type="submit" disabled={loading} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-md px-8" data-testid="submit-order-button">
            {loading ? "Creating…" : "Create Order"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate("/orders")} className="border-zinc-300 rounded-md" data-testid="cancel-button">Cancel</Button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-zinc-200 bg-white p-6">
      <h3 className="text-lg font-bold tracking-tight mb-4">{title}</h3>
      <div className="grid grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Field({ label, children, span }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

